<!--
  登录面板组件 - 纯内容，无弹窗包装
  可嵌入 el-dialog 或 el-popover 中使用
-->
<template>
  <div class="login-panel">
    <div class="login-sparkle login-sparkle--left"></div>
    <div class="login-sparkle login-sparkle--right"></div>

    <div class="dialog-logo">
      <div class="logo-heading">
        <span class="logo-ai">AI</span>
        <span class="logo-text">文字转语音</span>
      </div>
      <div class="logo-subtitle">轻松创作你的专属声音</div>
    </div>

    <!-- 登录方式切换 -->
    <div class="login-mode-tabs" role="tablist">
      <TButton
        :variant="loginMode === 'wechat' ? 'primary' : 'secondary'"
        @click="switchLoginMode('wechat')"
        size="small"
      >
        微信扫码登录
      </TButton>
      <TButton
        :variant="loginMode === 'email' ? 'primary' : 'secondary'"
        @click="switchLoginMode('email')"
        size="small"
      >
        账号登录
      </TButton>
    </div>

    <div class="login-mode-stage">
      <Transition name="login-mode-fade" mode="out-in">
        <!-- 微信扫码登录 -->
        <div v-if="loginMode === 'wechat'" key="wechat" class="login-mode-panel wechat-login-container">
          <WechatLogin
            :on-success="handleWechatLoginSuccess"
            :on-error="handleWechatLoginError"
            @switch-to-email="switchLoginMode('email')"
            ref="wechatLoginRef"
          />
          <div class="login-mode-switch">
            <el-text size="small" type="info">
              打开微信扫一扫，确认后自动进入工作台；扫码后可绑定邮箱，之后支持账号登录
            </el-text>
          </div>
        </div>

        <div v-else key="email" class="login-mode-panel email-login-container">
          <!-- 邮箱登录表单 -->
          <el-form
            v-if="isLoginMode"
            id="loginForm"
            ref="loginFormRef"
            :model="loginForm"
            :rules="loginRules"
            label-position="top"
          >
            <el-form-item label="账号" prop="email">
              <TInput
                v-model="loginForm.email"
                placeholder="请输入电子邮箱/用户名"
              />
            </el-form-item>
            <el-form-item label="密码" prop="password">
              <TInput
                v-model="loginForm.password"
                type="password"
                placeholder="请输入密码"
                show-password
              />
            </el-form-item>

            <div v-if="loginError" class="login-error-message">
              <el-alert
                :title="loginError"
                type="error"
                :closable="false"
                show-icon
              />
            </div>

            <div class="form-actions">
              <TButton variant="primary" :loading="loading" @click="handleLogin" style="width: 100%">
                立即登录
              </TButton>
            </div>
          </el-form>

          <!-- 切换到微信登录 -->
          <div class="mode-switch">
            <TButton variant="text" @click="switchLoginMode('wechat')">
              没有账户？使用微信登录注册
            </TButton>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store'
import WechatLogin from '@/views/auth/WechatLogin.vue'
import { TButton, TInput } from '@/components/ui'

const emit = defineEmits<{
  (e: 'login-success', data: any): void
  (e: 'register-success', data: any): void
  (e: 'close'): void
}>()

const userStore = useUserStore()

const loginMode = ref<'email' | 'wechat'>('wechat')
const isLoginMode = ref(true)
const loading = ref(false)
const loginError = ref('')

const loginFormRef = ref<any>()
const wechatLoginRef = ref<any>()

const loginForm = reactive({
  email: '',
  password: ''
})

const loginRules = {
  email: [{ required: true, message: '请输入邮箱或用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
}

const switchLoginMode = (mode: 'email' | 'wechat') => {
  if (loginMode.value === 'wechat' && mode === 'email') {
    wechatLoginRef.value?.cleanup?.()
  }
  loginMode.value = mode
  loginError.value = ''
}

const handleLogin = async () => {
  if (!loginFormRef.value) return
  try {
    await loginFormRef.value.validate()
    loading.value = true
    loginError.value = ''
    await userStore.login({ email: loginForm.email, password: loginForm.password })
    wechatLoginRef.value?.cleanup?.()
    emit('login-success', { ...loginForm })
    emit('close')
  } catch (error: any) {
    const errorMsg = error.message || '登录失败，请检查账号密码'
    if (errorMsg.includes('未绑定邮箱') || errorMsg.includes('邮箱未绑定')) {
      loginError.value = '该账号未绑定邮箱，请使用微信登录后在账户设置中绑定邮箱'
    } else {
      loginError.value = errorMsg
    }
  } finally {
    loading.value = false
  }
}

const handleWechatLoginSuccess = async (data: any) => {
  try {
    await userStore.handleWechatLoginSuccess(data)
    ElMessage.success('微信登录成功')
    emit('login-success', data)
    emit('close')
  } catch {
    ElMessage.success('微信登录成功')
    emit('login-success', data)
    emit('close')
  }
}

const handleWechatLoginError = (error: any) => {
  ElMessage.error('微信登录失败')
  console.error('Wechat login error:', error)
}

// 暴露给父组件重置微信登录状态
const resetWechat = async () => {
  if (loginMode.value === 'wechat') {
    await nextTick()
    wechatLoginRef.value?.reset?.()
  }
}

const cleanupWechat = async () => {
  if (loginMode.value === 'wechat') {
    await nextTick()
    wechatLoginRef.value?.cleanup?.()
  }
}

defineExpose({ resetWechat, cleanupWechat })
</script>

<style scoped>
.login-panel {
  position: relative;
  height: 100%;
  padding: 4px 0 0;
  isolation: isolate;
}

.login-panel::before {
  content: "";
  position: absolute;
  inset: 74px 0 8px;
  z-index: -1;
  border: 0;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.38);
  box-shadow: none;
}

.dialog-logo {
  text-align: center;
  margin-bottom: 10px;
}

.logo-heading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  overflow: visible;
}

.logo-ai {
  display: inline-flex;
  align-items: center;
  color: transparent;
  font-size: 30px;
  font-style: italic;
  font-weight: 900;
  line-height: 1.18;
  letter-spacing: 0;
  padding: 2px 4px 3px;
  background: linear-gradient(135deg, #22d3ee 0%, #4886ff 54%, #2563f5 100%);
  background-clip: text;
  -webkit-background-clip: text;
  text-shadow: 0 10px 24px rgba(72, 134, 255, 0.22);
  transform: skew(-8deg);
}

.logo-text {
  font-size: 27px;
  line-height: 1.18;
  font-weight: 800;
  color: #2563f5;
  letter-spacing: 0;
}

.logo-subtitle {
  margin-top: 2px;
  color: #7a8497;
  font-size: 14px;
  line-height: 1.5;
}

.login-mode-tabs {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto 14px;
  padding: 5px;
  border: 1px solid rgba(72, 134, 255, 0.14);
  border-radius: 999px;
  background: rgba(239, 245, 255, 0.78);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82);
}

.login-mode-tabs :deep(.el-button) {
  min-width: 128px;
  height: 38px;
  margin-left: 0;
  border-radius: 999px;
  border: 0;
  font-size: 14px;
  letter-spacing: 0;
}

.login-mode-tabs :deep(.el-button--primary) {
  background: linear-gradient(135deg, #38bdf8 0%, #4886ff 100%);
  box-shadow: 0 10px 24px rgba(72, 134, 255, 0.26);
}

.login-mode-tabs :deep(.el-button--default) {
  color: #526173;
  background: transparent;
}

.login-mode-tabs :deep(.el-button--default:hover) {
  color: #2563f5;
  background: rgba(255, 255, 255, 0.76);
}

.login-mode-stage {
  position: relative;
  height: 346px;
  overflow: hidden;
  padding: 4px 0 0;
}

.login-mode-panel {
  width: 100%;
  height: 100%;
}

.email-login-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.login-mode-fade-enter-active,
.login-mode-fade-leave-active {
  transition:
    opacity 220ms var(--tts-ease-out),
    transform 220ms var(--tts-ease-out),
    filter 220ms var(--tts-ease-out);
}

.login-mode-fade-enter-from {
  opacity: 0;
  filter: blur(4px);
  transform: translateY(12px) scale(0.985);
}

.login-mode-fade-leave-to {
  opacity: 0;
  filter: blur(4px);
  transform: translateY(-12px) scale(0.985);
}

.wechat-login-container {
  text-align: center;
}

.login-mode-switch {
  width: fit-content;
  max-width: 100%;
  margin: 12px auto 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.login-mode-switch :deep(.el-text) {
  color: #7a8497;
  font-size: 13px;
}

#loginForm {
  width: min(344px, 100%);
  max-width: 344px;
  margin: 0 auto;
  padding: 6px 0 0;
}

#loginForm :deep(.el-form-item) {
  margin-bottom: 14px;
}

#loginForm :deep(.el-form-item__label) {
  color: #364153;
  font-weight: 650;
  line-height: 1;
  margin-bottom: 9px;
}

#loginForm :deep(.el-input__wrapper) {
  height: 48px;
  border-radius: 16px;
  border-color: rgba(72, 134, 255, 0.16);
  background: rgba(248, 251, 255, 0.88);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92), 0 8px 18px rgba(72, 134, 255, 0.06);
}

#loginForm :deep(.el-input__wrapper:hover),
#loginForm :deep(.el-input__wrapper.is-focus) {
  border-color: rgba(72, 134, 255, 0.5);
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.12);
}

.login-error-message {
  margin-bottom: var(--tts-space-4);
}

.form-actions {
  margin-top: 16px;
}

.form-actions :deep(.el-button) {
  height: 48px;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
  background: linear-gradient(135deg, #38bdf8 0%, #4886ff 100%);
  box-shadow: 0 16px 30px rgba(72, 134, 255, 0.28);
}

.mode-switch {
  text-align: center;
  width: min(344px, 100%);
  margin: 18px auto 0;
  padding-top: 16px;
  border-top: 1px solid rgba(72, 134, 255, 0.1);
}

.mode-switch :deep(.el-button) {
  color: #4886ff;
  letter-spacing: 0;
}

.login-sparkle {
  display: none;
}

.login-sparkle--left {
  left: -22px;
  top: 88px;
}

.login-sparkle--right {
  right: -18px;
  top: 166px;
  width: 74px;
  height: 74px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.84), rgba(235, 245, 255, 0.48)),
    repeating-linear-gradient(45deg, rgba(72, 134, 255, 0.13) 0 5px, transparent 5px 10px);
  transform: rotate(12deg);
}

@media (max-width: 560px) {
  .logo-heading {
    gap: 8px;
  }

  .logo-ai {
    font-size: 28px;
  }

  .logo-text {
    font-size: 26px;
  }

  .login-mode-tabs {
    width: 100%;
  }

  .login-mode-tabs :deep(.el-button) {
    flex: 1;
    min-width: 0;
    padding: 0 10px;
  }

  .login-mode-stage {
    height: 346px;
  }
}
</style>
